import React from 'react';
import { Link } from 'react-router-dom';

const STEPS = [
  {
    n: '1',
    title: 'Capture',
    desc: 'A thin managed package records every flow failure in your org as a Flow_Failure__c record. Nothing leaves Salesforce but the error itself — never record data.',
  },
  {
    n: '2',
    title: 'Diagnose',
    desc: 'The diagnosis engine runs in Apex, on the failure record, and reads your ForceSight context graph — field types, references, what changed in recent scans — to name the root cause in your org\'s terms.',
  },
  {
    n: '3',
    title: 'Fix',
    desc: 'Every diagnosis states the fix and cites its evidence: the field, the flow element, the metadata change. Confirmed or Probable — never a bare stack trace.',
  },
];

const FEATURES = [
  {
    title: 'Every failure, captured',
    desc: 'Record-triggered, scheduled, screen, and autolaunched flows — every unhandled fault becomes a Flow_Failure__c record with the error, the flow, the element, and the interview context.',
  },
  {
    title: 'Automatic root-cause diagnosis',
    desc: 'A library of error analyzers — STRING_TOO_LONG, UNABLE_TO_LOCK_ROW, missing permissions, governor limits, inactive users, and more — runs on each failure and writes the cause in plain English.',
  },
  {
    title: 'Confidence you can act on',
    desc: 'Every diagnosis is graded Confirmed or Probable based on the evidence gathered, so you know when to fix immediately and when to verify first.',
  },
  {
    title: 'Recurrence intelligence',
    desc: 'The same failure firing 112 times is one problem, not 112. Failures group by flow and error family with first-seen, last-seen, and frequency.',
  },
  {
    title: 'Context-aware, not stack-trace deep',
    desc: 'Connected to the ForceSight Context Engine, diagnoses cite your field types, references, and recent metadata changes — "this broke because that changed."',
  },
  {
    title: 'Know before your users do',
    desc: 'Alerts on new failures and a trends dashboard for top failing flows, diagnosis rate, and time-to-resolution.',
  },
];

const FlowRadarPage = () => (
  <div className="radar-page">
    <div className="radar-hero">
      <p className="radar-eyebrow">ForceSight Flow Radar</p>
      <h1>Flow failures are silent. Your diagnosis shouldn&rsquo;t be.</h1>
      <p className="radar-lede">
        Flow Radar watches your Salesforce org for flow failures, diagnoses the
        root cause automatically, and tells you exactly what broke, why it
        broke, and how to fix it &mdash; before your users notice. On the
        failure record, inside your org, grounded in your metadata.
      </p>
      <a className="radar-cta" href="mailto:jimbike@gmail.com?subject=Flow%20Radar%20waitlist">
        Join the waitlist
      </a>
    </div>

    <div className="radar-steps">
      {STEPS.map((s) => (
        <div key={s.n} className="glass-card radar-step">
          <span className="radar-step-n">{s.n}</span>
          <h3>{s.title}</h3>
          <p>{s.desc}</p>
        </div>
      ))}
    </div>

    <div className="radar-example glass-card">
      <h2>What a diagnosis looks like</h2>
      <pre>
        <code>{`STRING_TOO_LONG → Confirmed
Element Assign_Summary_Field writes Case.Summary__c, a Text(255)
field, from a template that concatenates Subject +
Request_List__r.Batch_Notes__c — which became a 32k long-text
field in scan #46. Values now routinely exceed 255.

To fix this: truncate with LEFT(..., 255) in the assignment, or retype
Summary__c to Long Text Area.`}</code>
      </pre>
    </div>

    <div className="radar-features-block">
      <h2 className="radar-section-title">Everything Flow Radar does</h2>
      <div className="radar-features">
        {FEATURES.map((f) => (
          <div key={f.title} className="glass-card radar-feature">
            <h3>{f.title}</h3>
            <p>{f.desc}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="radar-context glass-card">
      <h2>Better together with the Context Engine</h2>
      <p>
        Flow Radar works standalone. Connected to the free ForceSight Context
        Engine, it gets smarter: the diagnosis engine reads your org&rsquo;s
        context graph, so instead of &ldquo;a field was too small,&rdquo; you
        get &ldquo;<em>this</em> field, retyped in <em>that</em> deployment,
        broke <em>this</em> assignment.&rdquo; Your metadata never leaves your
        org &mdash; only the error does.
      </p>
    </div>

    <p className="radar-footer-links">
      <Link to="/">Home</Link> &middot; <Link to="/pricing">Pricing</Link>
    </p>

    <style>{`
      .radar-page { max-width: 860px; margin: 0 auto; padding: 4rem 1.5rem 5rem; display: flex; flex-direction: column; gap: 2.5rem; }
      .radar-eyebrow { font-family: var(--font-mono); font-size: .78rem; letter-spacing: .14em; text-transform: uppercase; color: var(--force-cyan); font-weight: 600; margin: 0 0 .6rem; }
      .radar-hero h1 { font-family: var(--font-display); font-size: clamp(1.8rem, 4.5vw, 2.6rem); letter-spacing: -.02em; line-height: 1.15; color: var(--star-white); margin: 0 0 .8rem; }
      .radar-lede { color: var(--gray-400); font-size: 1.1rem; line-height: 1.6; max-width: 58ch; margin: 0 0 1.4rem; }
      .radar-cta { display: inline-block; background: var(--force-cyan); color: #062033; font-weight: 700; border-radius: 8px; padding: .65rem 1.3rem; text-decoration: none; }
      .radar-steps { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; }
      .radar-step { padding: 1.3rem; }
      .radar-step-n { font-family: var(--font-mono); color: var(--force-cyan); font-weight: 700; }
      .radar-step h3 { color: var(--star-white); margin: .4rem 0; }
      .radar-step p { color: var(--gray-400); font-size: .92rem; line-height: 1.6; }
      .radar-example { padding: 1.5rem; }
      .radar-example h2 { color: var(--star-white); font-size: 1.15rem; margin: 0 0 .8rem; }
      .radar-example pre { overflow-x: auto; background: rgba(4, 12, 28, .6); border: 1px solid var(--line); border-radius: 8px; padding: 1rem; }
      .radar-example code { font-family: var(--font-mono); font-size: .82rem; line-height: 1.65; color: var(--star-white); }
      .radar-section-title { color: var(--star-white); font-size: 1.3rem; letter-spacing: -.01em; margin: 0 0 1rem; }
      .radar-features { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1rem; }
      .radar-feature { padding: 1.2rem 1.3rem; }
      .radar-feature h3 { color: var(--star-white); font-size: 1rem; margin: 0 0 .4rem; }
      .radar-feature p { color: var(--gray-400); font-size: .9rem; line-height: 1.6; margin: 0; }
      .radar-context { padding: 1.5rem 2rem; }
      .radar-context h2 { color: var(--star-white); font-size: 1.15rem; margin: 0 0 .6rem; }
      .radar-context p { color: var(--gray-400); line-height: 1.7; margin: 0; max-width: 68ch; }
      .radar-footer-links { text-align: center; color: var(--gray-400); }
      .radar-footer-links a { color: var(--force-cyan); text-decoration: none; }
    `}</style>
  </div>
);

export default FlowRadarPage;
